import React, { useState } from 'react';
import { useCrud } from '../lib/useCrud';
import api from '../lib/api';
import { Autocomplete } from '../components/Autocomplete';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ClientLink } from '../components/links/EntityLinks';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Edit, Trash2, MoreVertical, Shield, Loader2, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';

const issuerOptions = ["Let's Encrypt", 'Comodo', 'DigiCert', 'GeoTrust', 'Sectigo', 'GoDaddy', 'Other'];
const typeOptions = [{ value: 'DV', label: 'Domain Validated (DV)' }, { value: 'OV', label: 'Organization Validated (OV)' }, { value: 'EV', label: 'Extended Validation (EV)' }, { value: 'Wildcard', label: 'Wildcard' }];
const emptyForm = { domain: '', issuer: "Let's Encrypt", type: 'DV', client_name: '', issued_date: '', expiry_date: '', auto_renew: true };

const getDaysUntilExpiry = (date) => { if (!date) return 999; return Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24)); };

export const SSLPage = () => {
  const { data: certs, loading, fetchData, create, update, remove } = useCrud('/ssl');
  const confirm = useConfirmation();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('ssl_certificates.create');
  const canEdit = hasPermission('ssl_certificates.edit');
  const canDelete = hasPermission('ssl_certificates.delete');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedCert) {
        await update(selectedCert.id, formData);
        toast.success('SSL updated');
      } else {
        await create(formData);
        toast.success('SSL added');
      }
      fetchData();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Operation failed';
      toast.error(detail);
    }
  };

  const handleDelete = async (c) => {
    const confirmed = await confirm({ title: 'Remove SSL Certificate', description: `Are you sure you want to remove SSL for "${c.domain}"? This action cannot be undone.` });
    if (!confirmed) return;
    try { await remove(c.id); toast.success('SSL removed'); fetchData(); } catch { toast.error('Delete failed'); }
  };

  const handleEdit = (c) => {
    setSelectedCert(c);
    setFormData({ domain: c.domain || '', issuer: c.issuer || "Let's Encrypt", type: c.type || 'DV', client_name: c.client_name || '', issued_date: (c.issued_date || '').split('T')[0], expiry_date: (c.expiry_date || '').split('T')[0], auto_renew: c.auto_renew ?? true });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const resetForm = () => { setFormData(emptyForm); setSelectedCert(null); setIsEditing(false); };

  const getExpiryBadge = (date) => {
    const days = getDaysUntilExpiry(date);
    if (days < 0) return <Badge className="bg-red-500/10 text-red-500">Expired</Badge>;
    if (days <= 30) return <Badge className="bg-amber-500/10 text-amber-500">{days}d left</Badge>;
    return <Badge className="bg-emerald-500/10 text-emerald-500">{days}d</Badge>;
  };

  const getTypeBadge = (type) => {
    const cfg = { DV: 'bg-blue-500/10 text-blue-500', OV: 'bg-purple-500/10 text-purple-500', EV: 'bg-emerald-500/10 text-emerald-500', Wildcard: 'bg-amber-500/10 text-amber-500' };
    return <Badge className={cfg[type] || cfg.DV}>{type}</Badge>;
  };

  const filtered = certs.filter(c => !searchQuery || c.domain?.toLowerCase().includes(searchQuery.toLowerCase()) || c.client_name?.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ssl-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">SSL Certificates</h1><p className="text-muted-foreground">Track SSL certificate status and renewals</p></div>
        <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
          {canCreate && (<DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-ssl-btn"><Plus className="h-4 w-4 mr-2" />Add SSL</Button></DialogTrigger>)}
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{isEditing ? 'Edit SSL' : 'Add SSL Certificate'}</DialogTitle><DialogDescription>Enter SSL certificate details</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2"><Label>Domain *</Label><Input value={formData.domain} onChange={e => setFormData({ ...formData, domain: e.target.value })} placeholder="example.com" required data-testid="ssl-domain" /></div>
                <div className="space-y-2"><Label>Issuer</Label><Select value={formData.issuer} onValueChange={v => setFormData({ ...formData, issuer: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{issuerOptions.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Type</Label><Select value={formData.type} onValueChange={v => setFormData({ ...formData, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{typeOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Client *</Label>
                  <Autocomplete
                    value={formData.client_name}
                    onChange={v => setFormData({ ...formData, client_name: v })}
                    fetchOptions={async (q) => { const res = await api.get(`/clients?search=${q}&limit=10`); return res.data.success ? res.data.data.map(c => c.company_name || c.name) : []; }}
                    placeholder="Search clients..."
                    data-testid="ssl-client"
                  />
                </div>
                <div className="space-y-2"><Label>Issued</Label><Input type="date" value={formData.issued_date} onChange={e => setFormData({ ...formData, issued_date: e.target.value })} /></div>
                <div className="space-y-2 col-span-2"><Label>Expires *</Label><Input type="date" value={formData.expiry_date} onChange={e => setFormData({ ...formData, expiry_date: e.target.value })} required data-testid="ssl-expiry" /></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="ssl-submit-btn">{isEditing ? 'Update' : 'Add'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card><CardContent className="py-4"><div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search SSL certificates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="ssl-search" /></div></CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center"><Shield className="h-5 w-5 text-green-500" /></div><div><p className="text-2xl font-bold">{filtered.length}</p><p className="text-xs text-muted-foreground">Total Certificates</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Shield className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold">{filtered.filter(c => c.auto_renew).length}</p><p className="text-xs text-muted-foreground">Auto-Renew</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold">{filtered.filter(c => getDaysUntilExpiry(c.expiry_date) <= 30).length}</p><p className="text-xs text-muted-foreground">Expiring Soon</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        filtered.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><Shield className="h-12 w-12 mb-4 opacity-50" /><p>No SSL certificates found</p></div> :
        <Table data-testid="ssl-table"><TableHeader><TableRow><TableHead>Domain</TableHead><TableHead>Client</TableHead><TableHead>Issuer</TableHead><TableHead>Type</TableHead><TableHead>Expires</TableHead><TableHead>Auto-Renew</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{filtered.map(c => (
          <TableRow key={c.id} data-testid={`ssl-row-${c.id}`}>
            <TableCell><div className="flex items-center gap-2"><Shield className="h-4 w-4 text-green-500" /><span className="font-medium">{c.domain}</span></div></TableCell>
            <TableCell><ClientLink id={c.client_id} name={c.client_name} /></TableCell>
            <TableCell>{c.issuer}</TableCell>
            <TableCell>{getTypeBadge(c.type)}</TableCell>
            <TableCell>{getExpiryBadge(c.expiry_date)}</TableCell>
            <TableCell>{c.auto_renew ? <Badge className="bg-emerald-500/10 text-emerald-500">Yes</Badge> : <Badge className="bg-gray-500/10 text-gray-400">No</Badge>}</TableCell>
            <TableCell>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (<DropdownMenuItem onClick={() => handleEdit(c)} data-testid={`ssl-edit-${c.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                {canDelete && (<DropdownMenuItem onClick={() => handleDelete(c)} className="text-destructive focus:text-destructive" data-testid={`ssl-delete-${c.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
              </DropdownMenuContent></DropdownMenu>
            </TableCell>
          </TableRow>))}</TableBody></Table>}
      </CardContent></Card>
    </div>
  );
};
